import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Snackbar,
  Alert,
} from '@mui/material';
import { useGetPreviouslyClosedQuarterQuery, useStartQuarterMutation } from '../rnrApiSlice';
import toast from 'react-hot-toast';

const getCurrentQuarter = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  if (month <= 3) return 1;
  if (month <= 6) return 2;
  if (month <= 9) return 3;
  return 4;
};

const StartQuarterDialog = ({ open, onClose, isDeactivateButtonClicked, setIsDeactivateButtonClicked }) => {
  const { data: previousQuarterData } = useGetPreviouslyClosedQuarterQuery();
  const [quarterOptions, setQuarterOptions] = useState([]);
  const currentQuarter = getCurrentQuarter();
  const [selectedQuarter, setSelectedQuarter] = useState(1);
  const [quarter, setQuarter] = useState(selectedQuarter);
  const [year, setYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [categoriesSelection, setCategoriesSelection] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startQuarter] = useStartQuarterMutation();

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (previousQuarterData?.data) {
      const previousQuarter = previousQuarterData.data.time.quarter;
      const previousYear = previousQuarterData.data.time.year;

      if (previousQuarter === 4) {
        setQuarterOptions([1, 2, 3, 4]);
        setYear(previousYear + 1);
      } else {
        setQuarterOptions([2, 3, 4].filter((q) => q > previousQuarter));
      }

      setQuarter((previousQuarter % 4) + 1);
    }
  }, [previousQuarterData]);

 const handleSubmit = async () => {
  if (!startDate || !endDate || categoriesSelection.length === 0) {
    setSnackbar({ open: true, message: 'Please fill all the required fields.', severity: 'error' });
    return;
  }

  setIsLoading(true);
  try {
    const response = await startQuarter({ quarter, year, startDate, endDate, categoriesSelection });
    toast.success(response.data.message);
    onClose();
  } catch (error) {
    setSnackbar({ open: true, message: 'Something went wrong. Please try again.', severity: 'error' });
  } finally {
    setIsLoading(false);
  }
};


  useEffect(() => {
    if (isDeactivateButtonClicked) {
      resetState();
    }
  }, [isDeactivateButtonClicked]);

  const resetState = () => {
    setEndDate('');
    setStartDate('');
    setCategoriesSelection([]);
    setIsDeactivateButtonClicked(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    
    <Dialog open={open} onClose={handleClose}>
      <Snackbar
          open={snackbar.open}
          autoHideDuration={1000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2000,
          }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      <DialogTitle>Start RnR Quarter</DialogTitle>
       
      <DialogContent sx={{ position: 'relative' }}>
        {/* Snackbar rendered inside dialog content */}
       

        <FormControl fullWidth margin="normal">
          <InputLabel>Quarter *</InputLabel>
          <Select value={quarter} label="Quarter" onChange={(e) => setQuarter(e.target.value)} required fullWidth>
            {quarterOptions?.map((q) => (
              <MenuItem key={q} value={q}>Q{q}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Year"
          type="number"
          fullWidth
          margin="normal"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          required
        />

        <TextField
          label="Start Date"
          type="date"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: new Date().toISOString().split('T')[0] }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          required
        />

        <TextField
          label="End Date"
          type="date"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: startDate }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          required
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Select categories *</InputLabel>
          <Select
            multiple
            label="Select categories"
            fullWidth
            value={categoriesSelection}
            onChange={(e) => setCategoriesSelection(e.target.value)}
            renderValue={(selected) => selected.join(', ')}
            required
          >
            {['Innovator', 'Mentor', 'Team Work', 'Newbie', 'Extra Miler'].map((category) => (
              <MenuItem key={category} value={category}>
                <Checkbox checked={categoriesSelection.includes(category)} />
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" type='submit' disabled={isLoading}>
          {isLoading ? 'Starting...' : 'Start'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartQuarterDialog;
